import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useForm } from '@mantine/form';
import { TextInput, PasswordInput, Button, Paper, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Lock, Mail, User } from 'lucide-react';

const RegisterPage: React.FC = () => {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm({
    initialValues: {
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validate: {
      email: (value) => (value.length < 5 ? 'Invalid email' : null),
      password: (value) => (value.length < 6 ? 'Password too short' : null),
      confirmPassword: (value, values) =>
        value !== values.password ? 'Passwords do not match' : null,
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    const { error } = await signUp(values.email, values.password);

    if (error) {
      showNotification({
        title: 'Registration Error',
        message: error,
        color: 'red',
      });
      return;
    }

    showNotification({
      title: 'Registration Successful',
      message: 'You can now log in with your new account.',
      color: 'green',
    });

    navigate('/login');
  };

  return (
    <div className="py-12 px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            <span className="bg-linear-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              LSAT Gains
            </span>
          </h2>
          <h1 className="mt-6 text-2xl font-bold text-gray-900">
            Create a new account
          </h1>
          <p className="mt-2 text-gray-600">
              Join thousands of students preparing for the LSAT
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-2xl sm:px-10 border border-gray-100">
          <form className="space-y-6" onSubmit={form.onSubmit(handleSubmit)}>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-1">
              <div>
                <label htmlFor="full-name" className="block text-sm font-medium text-gray-700">
                  Full name
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="fullName"
                    id="full-name"
                    autoComplete="full-name"
                    // required
                    {...form.getInputProps('fullName')}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="register-email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="register-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  {...form.getInputProps('email')}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="register-password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="register-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  {...form.getInputProps('password')}
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Must be at least 8 characters with at least one number and one special character
              </p>
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                Confirm password
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirm-password"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  {...form.getInputProps('confirmPassword')}
                  className={`block w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                    form.getValues().password && form.getValues().confirmPassword && 
                    form.getValues().password !== form.getValues().confirmPassword 
                      ? 'border-red-300' 
                      : 'border-gray-300'
                  }`}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              {form.getValues().password && form.getValues().confirmPassword && 
                form.getValues().password !== form.getValues().confirmPassword && (
                <p className="mt-1 text-xs text-red-500">
                  Passwords do not match
                </p>
              )}
            </div>

            {/* <div className="flex items-center">
              <input
                id="agree-terms"
                name="agreeTerms"
                type="checkbox"
                required
                // checked={registerForm.agreeTerms}
                // onChange={handleRegisterChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="agree-terms" className="ml-2 block text-sm text-gray-700">
                I agree to the <a href="/terms-of-service" className="text-indigo-600 hover:text-indigo-500">Terms of Service</a> and <a href="/privacy" className="text-indigo-600 hover:text-indigo-500">Privacy Policy</a>
              </label>
            </div> */}

            <div>
              <button
                type="submit"
                disabled={form.getValues().password !== form.getValues().confirmPassword}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-white bg-gradient-to-r from-indigo-600 to-purple-600 font-medium
                  ${form.getValues().password !== form.getValues().confirmPassword && form.getValues().confirmPassword 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                  }`}
              >
                Create account
              </button>
            </div>
          </form>
        </div>
        
        {/* Toggle between login and register */}
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Already have an account?
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="ml-2 font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none"
            >
              Sign in
            </button>
          </p>
        </div>
        
        {/* Back to home link */}
        <div className="mt-4 text-center">
          <a onClick={() => navigate('/')} className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500">
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to home
          </a>
        </div>
      </div>
      
      {/* Background elements */}
      {/* <div className="absolute top-0 right-0 hidden lg:block w-1/3 h-full bg-gradient-to-bl from-indigo-100 to-transparent opacity-50 rounded-bl-full"></div>
      <div className="absolute bottom-0 left-0 hidden lg:block w-1/4 h-1/2 bg-gradient-to-tr from-purple-100 to-transparent opacity-50 rounded-tr-full"></div> */}
    </div>
  );
};

export default RegisterPage;
